import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import Sidebar from "@/components/Sidebar";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import BillGeneration from "@/pages/BillGeneration";
import InvoicePreview from "@/pages/InvoicePreview";
import BillHistory from "@/pages/BillHistory";
import DuesTracker from "@/pages/DuesTracker";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import CustomerLedger from "@/pages/CustomerLedger";
import BulkBillGeneration from "@/pages/BulkBillGeneration";
import PaymentReceipt from "@/pages/PaymentReceipt";
import BulkPrint from "@/pages/BulkPrint";
import ManageUsers from "@/pages/ManageUsers";
import AuditLog from "@/pages/AuditLog";
import RateSettings from "@/pages/RateSettings";
import Statement from "@/pages/Statement";

function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-950">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-zinc-500 text-sm font-mono">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <div className="flex h-screen bg-zinc-50">
      <aside className="hidden md:flex w-64 flex-shrink-0">
        <Sidebar />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-zinc-950 border-none">
          <Sidebar onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="md:hidden h-14 bg-zinc-950 flex items-center px-4 flex-shrink-0">
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen(true)}
            className="text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-white font-heading font-bold ml-3 text-lg tracking-tight">
            SUPERFLAME
          </span>
        </header>

        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/customers/:id/ledger" element={<CustomerLedger />} />
            <Route path="/bills/generate" element={<BillGeneration />} />
            <Route path="/bills/bulk-generate" element={<BulkBillGeneration />} />
            <Route path="/bills/invoice/:id" element={<InvoicePreview />} />
            <Route path="/bills/receipt/:id" element={<PaymentReceipt />} />
            <Route path="/bills/bulk-print" element={<BulkPrint />} />
            <Route path="/bills/history" element={<BillHistory />} />
            <Route path="/dues" element={<DuesTracker />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/manage-users" element={<ManageUsers />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/rate-settings" element={<RateSettings />} />
            <Route path="/statement" element={<Statement />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

export default App;
